// src/components/ProtectedRoute.tsx
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import React from "react";

// 保護されたルートのプロパティ型定義
interface ProtectedRouteProps {
  requiredRoles?: string[];
}

/**
 * 保護されたルートコンポーネント
 * 認証されていないユーザーはログインページにリダイレクトされる
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ requiredRoles }) => {
  const { currentUser, userRole, loading, isPublicPath } = useAuth();
  const location = useLocation();

  // デバッグログの出力
  console.log('🛡️ ProtectedRoute check:');
  console.log('  - currentUser:', currentUser ? 'authenticated' : 'not authenticated');
  console.log('  - loading:', loading);
  console.log('  - isPublicPath:', isPublicPath);
  console.log('  - location.hash:', location.hash);
  console.log('  - location.pathname:', location.pathname);

  // ロード中の表示
  if (loading) {
    console.log('⏳ ProtectedRoute - showing loading screen');
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>認証情報を確認中...</p>
      </div>
    );
  }

  // ✅ 公開パスなら認証チェックをスキップして通過
  if (isPublicPath) {
    console.log('🔓 ProtectedRoute - public path detected, allowing access');
    return <Outlet />;
  }

  // ✅ 未認証の場合はログインページへリダイレクト
  if (!currentUser) {
    console.log('❌ ProtectedRoute - user not authenticated, redirecting to login');
    
    // HashRouterでの状態保持：元のパスを保存してログイン後にリダイレクト
    const currentPath = location.hash.replace(/^#/, '') || '/';
    console.log('📍 Saving redirect path:', currentPath);
    
    return <Navigate to="/login" state={{ from: { pathname: currentPath } }} replace />;
  }

  // ✅ ロールチェック（requiredRolesが指定されている場合）
  if (requiredRoles && requiredRoles.length > 0) {
    console.log('🔐 ProtectedRoute - checking role requirements:', requiredRoles);
    console.log('🔐 User role:', userRole);
    
    if (!userRole || !requiredRoles.includes(userRole)) {
      console.log('❌ ProtectedRoute - insufficient role, redirecting to unauthorized');
      return <Navigate to="/unauthorized" replace />;
    }
  }

  console.log('✅ ProtectedRoute - all checks passed, rendering protected content');
  
  // 認証済みかつ権限があれば子ルートを表示
  return <Outlet />;
};

export default ProtectedRoute;